import styled, {css} from 'styled-components';
import {Button, Space} from 'antd';
import {agentSenderStyles, iconColor, typographyStyles} from '../../utils/styles';
import Fade from '../animations/Fade';

const bubbleStyles = css`
  background-color: transparent;
  padding: 8px;
  border-radius: 8px;
  color: ${props => props.theme.colors.neutral_10};
  font-size: 14px;
  line-height: 22px;
  min-height: unset;
`;

// Fuses Fade's motion animation directly onto this element (rather than
// wrapping it as a child) so a `transform`-based enter/exit animation can be
// used safely: a transform on an ANCESTOR of a `position: fixed` element
// changes its containing block away from the viewport, but a transform on
// the fixed element itself does not.
const StyledFullWidthAgentChatContainer = styled(Fade)`
  background: linear-gradient(180deg, #F5F3FE 0%, #F6F5FE 20%, #F8F7FE 50%, #FAFAFB 100%);
  display: flex;
  flex-direction: column;
  inset: 0;
  position: fixed;
  z-index: 1500;
`;

const StyledFullWidthAgentChatHeader = styled.div`
  align-items: center;
  display: flex;
  flex: none;
  gap: 8px;
  height: var(--agent-chat-header-height);
  justify-content: flex-end;
  padding: 0 24px;
  width: 100%;
`;

// Sized like the close button beside it, and labelled, with the chips' hover: leaving a conversation is less obvious
// than closing the view.
const StyledFullWidthAgentChatNewChatButton = styled(Button)`
  align-items: center;
  background: ${props => props.theme.colors.neutral_1};
  border: 1px solid ${props => props.theme.colors.neutral_4};
  display: flex;
  height: 28px;
  ${typographyStyles.body.regular};

  .ant-btn-icon {
    display: flex;
  }

  svg {
    height: 16px;
    width: 16px;
  }

  &:not(:disabled) {
    color: ${props => props.theme.colors.neutral_8};

    svg {
      ${props => iconColor(props.theme.colors.neutral_8)}
    }
  }

  &:not(:disabled):hover {
    background: ${props => props.theme.colors.neutral_1};
    border-color: ${props => props.theme.colors.primary_5} !important;
    color: ${props => props.theme.colors.primary_5} !important;

    svg {
      ${props => iconColor(props.theme.colors.primary_5, true)}
    }
  }

  &:disabled svg {
    ${props => iconColor(props.theme.colors.neutral_5)}
  }
`;

const StyledFullWidthAgentChatCloseButton = styled(Button)`
  align-items: center;
  background: ${props => props.theme.colors.neutral_1};
  border: 1px solid ${props => props.theme.colors.neutral_4};
  display: flex;
  height: 28px;
  justify-content: center;
  width: 28px !important;

  .ant-btn-icon {
    display: flex;
  }

  &:hover {
    background: ${props => props.theme.colors.neutral_1};
  }

  svg {
    ${props => iconColor(props.theme.colors.neutral_7)}
  }
`;

const StyledFullWidthAgentChatBody = styled(Space)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 19px;
  width: 100%;

  & > .ant-space-item {
    width: 100%;
  }

  .ant-bubble {
    padding: 0 5px;

    &.bubble-file-message,
    &.bubble-error-message,
    &.bubble-confirmation-message,
    &.bubble-options-message {
      .ant-bubble-content {
        background-color: transparent;
        min-height: unset;
        padding: 0;
      }
    }

    &.bubble-confirmation-message,
    &.bubble-options-message {
      .ant-bubble-content {
        width: 100%;
      }
    }

    .ant-attachment-list-card-type-overview {
      border-radius: 8px;
    }

    &:not(.bubble-file-message):not(.bubble-error-message):not(.bubble-confirmation-message):not(.bubble-options-message) {
      .ant-bubble-content {
        ${bubbleStyles};
      }
    }

    &[role='user']:not(.bubble-file-message):not(.bubble-error-message):not(.bubble-confirmation-message):not(.bubble-options-message) {
      .ant-bubble-content {
        background-color: #fff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, .12);
      }
    }

    &[role='assistant']:not(.bubble-file-message):not(.bubble-error-message):not(.bubble-confirmation-message):not(.bubble-options-message) {
      .ant-bubble-content {
        background-color: rgba(131, 111, 255, .09);
        border: 1px solid rgba(131, 111, 255, .14);
      }
    }
  }

  .ant-bubble-content {
    min-height: unset;
    p {
      margin: 0;
    }
  }

  .ant-bubble-list {
    padding: 5px 0;
    width: 100%;
  }
`;

const StyledFullWidthAgentChatBodyContent = styled.div`
  margin: 0 auto;
  max-width: 720px;
  width: 100%;
`;

const StyledFullWidthAgentChatFooter = styled(Space)`
  bottom: 0;
  margin: 0 auto;
  max-width: 720px;
  padding: 24px 0;
  width: 100%;

  ${agentSenderStyles};

  .ant-sender {
    border: 1px solid ${props => props.theme.colors.neutral_3};
    box-shadow: 0 1px 4px rgba(0, 0, 0, .06);

    &:after {
      content: none;
    }
  }
`;

const StyledFullWidthAgentChatWaitingSpace = styled(Space)`
  align-items: center;
  color: ${props => props.theme.colors.neutral_7};
  display: flex;
  ${typographyStyles.footnote.description};

  .ant-spin-dot {
    display: flex;
    align-items: center;
    justify-content: center;
    div {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    svg {
      height: 16px !important;
      width: 16px !important;
      transform: unset !important;
    }
  }
`;

const StyledFullWidthAgentChatWaitingSpaceContainer = styled.div`
  min-height: 22px;

  ${StyledFullWidthAgentChatWaitingSpace} {
    padding: 0 5px;
  }
`;

const StyledFullWidthAgentChatConfirmation = styled.div`
  svg {
    margin-right: 8px;
    width: 16px;
    height: 16px;
  }

  & > .confirmation-message {
    ${bubbleStyles};
    background-color: rgba(131, 111, 255, .09);
    border: 1px solid rgba(131, 111, 255, .14);
    margin-bottom: 0;
    max-width: 100%;
    width: max-content;

    p {
      margin: 0;
    }

    &:has(+ div) {
      margin-bottom: 16px !important;
    }
  }
`;

export {
  StyledFullWidthAgentChatContainer,
  StyledFullWidthAgentChatHeader,
  StyledFullWidthAgentChatCloseButton,
  StyledFullWidthAgentChatNewChatButton,
  StyledFullWidthAgentChatBody,
  StyledFullWidthAgentChatBodyContent,
  StyledFullWidthAgentChatFooter,
  StyledFullWidthAgentChatWaitingSpace,
  StyledFullWidthAgentChatWaitingSpaceContainer,
  StyledFullWidthAgentChatConfirmation,
}
